import React from 'react'
import { User } from 'api/typings'
import CollapsibleSection from 'components/Shared/CollapsibleSection'
import { Loading, Flex, Box, Text, Label } from '@workwhile/ui'
import {
  useUserOutstandingBalance,
  useUserPaymentProfile,
  useUserPayments,
} from 'queries/payments'
import { useUnpaidShifts } from 'queries/unpaidShifts/useUnpaidShifts'
import { UserPaymentStatus } from 'api/payments'
import { UserPaymentsTable } from 'pages/UserPayments/components/UserPaymentsTable'
import { UserPaymentsScope } from 'pages/UserPayments/components/UserPaymentsScope'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6'
import { MoneyFlowVisualization } from './MoneyFlowVisualization/MoneyFlowVisualization'
import { UserPaymentFilters } from 'pages/UserPayments/components/UserPaymentFilters/UserPaymentFilters'
import { ColumnsFilter } from 'pages/UserPayments/components/ColumnsFilter/ColumnsFilter'
import { defs } from 'components/Shared/defs'
import { UnpaidShiftsTable } from 'pages/UnpaidShifts/components/UnpaidShiftsTable'
import { UnpaidShiftsProvider } from 'pages/UnpaidShifts/context/UnpaidShiftsProvider'

interface Props {
  user: User
}

const UserPayment = ({ user }: Props) => {
  const userId = user.id ? Number(user.id) : 0

  const { data: outstandingBalance } = useUserOutstandingBalance(
    {
      userId,
    },
    {
      enabled: !!user.id,
    }
  )

  const { data: paymentProfile } = useUserPaymentProfile(
    {
      userId,
    },
    {
      enabled: !!user.id,
    }
  )

  // Fetch unpaid shifts count
  const { data: unpaidShiftsData } = useUnpaidShifts(
    {
      userId,
      pageSize: 1, // Only need count, not the actual data
    },
    {
      enabled: !!user.id,
    }
  )

  // Fetch pending payments count
  const { data: pendingPaymentsData } = useUserPayments(
    {
      userId,
      status: [UserPaymentStatus.PENDING],
      pageSize: 1, // Only need count, not the actual data
    },
    {
      enabled: !!user.id,
    }
  )

  const unpaidShiftsCount = unpaidShiftsData?.totalRows ?? 0
  const pendingPaymentsCount = pendingPaymentsData?.totalRows ?? 0

  return (
    <CollapsibleSection title="Payments">
      {outstandingBalance && paymentProfile ? (
        <MoneyFlowVisualization
          user={user}
          outstandingBalance={outstandingBalance}
          paymentProfile={paymentProfile}
        />
      ) : (
        <Loading />
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          marginTop: defs.marginM,
          marginBottom: defs.marginXL,
        }}
      >
        <CollapsibleSection
          title={
            <Flex alignItems="center" style={{ gap: 8 }}>
              <span>Unpaid Shifts</span>
              {unpaidShiftsCount > 0 && (
                <Label size="small" variant="information">
                  {unpaidShiftsCount} shifts unpaid
                </Label>
              )}
            </Flex>
          }
        >
          <Text fontWeight="bold" color="warning" marginBottom={3}>
            NOTE: Actual earnings may differ from expected earnings due to
            bonuses or other adjustments.
          </Text>
          <Text fontWeight="bold" color="warning" marginBottom={3}>
            NOTE: Employer cancelled shifts are also not included yet.
          </Text>
          <NuqsAdapter>
            <UnpaidShiftsProvider
              initialUserId={user.id ? Number(user.id) : undefined}
            >
              <Box>
                <UnpaidShiftsTable />
              </Box>
            </UnpaidShiftsProvider>
          </NuqsAdapter>
        </CollapsibleSection>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          marginTop: defs.marginM,
          marginBottom: defs.marginXL,
        }}
      >
        <CollapsibleSection
          title={
            <Flex alignItems="center" style={{ gap: 8 }}>
              <span>User Payments</span>
              {pendingPaymentsCount > 0 && (
                <Label size="small" variant="information">
                  {pendingPaymentsCount} payments pending
                </Label>
              )}
            </Flex>
          }
        >
          <NuqsAdapter>
            <UserPaymentsScope
              initialUserId={user.id ? Number(user.id) : undefined}
              initialSelectedColumns={['provider', 'originated-at', 'comment']}
            >
              <Flex justifyContent="left" alignItems="center" mb={3}>
                <UserPaymentFilters hideUserFilter={true} />
              </Flex>
              <Flex justifyContent="right" alignItems="center" mb={3}>
                <ColumnsFilter />
              </Flex>
              <UserPaymentsTable />
            </UserPaymentsScope>
          </NuqsAdapter>
        </CollapsibleSection>
      </div>

      <hr />
    </CollapsibleSection>
  )
}

export default UserPayment
